import React from "react";

import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton as MUIIconButton, Stack, Typography, alpha, styled, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import PriceField from "components/elements/PriceField";
import { useTranslation } from "react-i18next";
import { addStringNumbers, compareStringNumber, subtractStringNumbers } from "utils";

import { IProps } from "./types";

export const IconButton = styled(MUIIconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.main,
  width: "30px",
  height: "30px",

  "&.Mui-disabled": {
    color: theme.palette.common.white,
    backgroundColor: "#C4C4C4",
  },
  "&:hover:not(.Mui-disabled)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
  },
}));

const inputSx = { maxWidth: "100px", minWidth: "70px" };

const PercentageInput = ({ value, onChange, min, max, error, index, handleRemoveOrder, mode, step = 1 }: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let originalValue = event.target.value;
    originalValue = originalValue.replace(/^0+(?=\d)/, "");

    onChange(originalValue);
  };

  const handleBlur = () => {
    if (value === undefined) return;

    const hasComma = value.includes(",");
    let numValue = Number(value.replace(",", "."));

    // Check min/max
    if (numValue < min) numValue = min;
    else if (numValue > max) numValue = max;

    // Round up to the nearest multiple of step
    numValue = Math.ceil(numValue / step) * step;

    // Return to original format
    let finalString = numValue.toString();
    if (hasComma) finalString = finalString.replace(".", ",");

    onChange(finalString);
  };

  const handleAdd = () => onChange(addStringNumbers(value, step));
  const handleRemove = () => onChange(subtractStringNumbers(value, step, min));

  const isDisabledRemove = React.useMemo(() => compareStringNumber(value, min, "<="), [value, min]);
  const isDisabledAdd = React.useMemo(() => compareStringNumber(value, max, ">="), [value, max]);

  const allowZero = React.useMemo(() => min < 1, [min]);
  const precision = React.useMemo(() => {
    const stepString = step.toString();
    const decimalIndex = stepString.indexOf(".");

    if (decimalIndex === -1) {
      return 0;
    }

    return stepString.length - decimalIndex - 1;
  }, [step]);

  return (
    <Box sx={{ pb: { xs: 0, sm: 1 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "start", sm: "center" }}
        spacing={{ xs: 1, sm: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"} width={isXs ? "100%" : undefined}>
          <Typography>{t(`dcaBots.create.limitOrder.label.${mode}`)}</Typography>
          <MUIIconButton
            sx={{ p: 0, display: isXs && index !== 0 ? undefined : "none" }}
            onClick={() => handleRemoveOrder(index)}>
            <HighlightOffIcon />
          </MUIIconButton>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          flex={1}
          alignItems={"center"}
          spacing={2}
          pb={{ xs: 1, sm: 0 }}>
          <Stack direction={"row"} alignItems={"center"} spacing={2} justifyContent={"center"} alignContent={"center"}>
            <PriceField
              value={value}
              size="small"
              autoComplete="off"
              precision={precision}
              onlyPositive
              allowZero={allowZero}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={!!error}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={inputSx}
            />
            <Stack direction={"row"} spacing={1}>
              <IconButton onClick={handleRemove} disabled={isDisabledRemove}>
                <RemoveIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleAdd} disabled={isDisabledAdd}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <MUIIconButton
            sx={{ display: !isXs && index !== 0 ? undefined : "none" }}
            onClick={() => handleRemoveOrder(index)}>
            <HighlightOffIcon />
          </MUIIconButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PercentageInput;
